import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { pageWidth } from "Payments/PaymentPage/PaymentFormScreen/styles.js";
import { motion } from "framer-motion";

import { colorIsLight } from "Utilities/Colors/colorUtils";
import { addHttpToUrl } from "Utilities/addHttpToUrl.js";

import { usePaymentPage } from "./PaymentPageContext";

export default function PageHeader({ isFooter }) {
  const {
    state: {
      network: { iconURL, website, name, city, state },
      fund: { colorScheme },
      headerIsBig,
      noteInfo,
      paymentParent,
    },
  } = usePaymentPage();

  const isSignup = paymentParent?.parent === "signups";
  const isBig = headerIsBig;
  const [height, setHeight] = useState();
  const [isMounted, setIsMounted] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const websiteSafe = addHttpToUrl(website);

  const backgroundIsDark =
    !isSignup &&
    colorScheme &&
    colorScheme.length &&
    !colorIsLight(colorScheme[Math.floor(colorScheme.length / 2)]);

  useEffect(() => {
    if (name) setIsMounted(true);
  }, [name]);

  const contentsRef = useRef();

  useEffect(() => {
    if (contentsRef.current) {
      const { height } = contentsRef.current.getBoundingClientRect();
      setHeight(height);
    }
  }, [isBig, isMounted]);

  const animationComplete = () => {
    if (!isBig) setHasAnimated(true);
  };

  if (!name) return null;
  if (isBig === undefined) return null;
  if (paymentParent?.hidePageHeader) return null;

  return (
    <Wrapper $isFooter={isFooter}>
      <TopPadding
        initial={{ height: isBig ? 80 : 0 }}
        animate={{ height: isBig ? 80 : 0 }}
      />
      <Inside animate={height && { height }}>
        <Contents
          ref={contentsRef}
          href={websiteSafe}
          target="_blank"
          rel="noreferrer"
          //prevents a_all.css legacy link transform
          className="not_default_link"
        >
          {isBig && (
            <Logo
              style={{ backgroundImage: `url(${iconURL})` }}
              layoutId={!hasAnimated && isMounted && height && "network_logo"}
              key={!hasAnimated && isMounted && height ? "2" : "1"}
            />
          )}
          <InfoContainer>
            <SidePadding
              initial={{ width: isBig ? 40 : isFooter ? 60 : 80 }}
              animate={{ width: isBig ? 40 : isFooter ? 60 : 80 }}
              onAnimationComplete={animationComplete}
            >
              {!isBig && (
                <LogoSmall
                  style={{ backgroundImage: `url(${iconURL})` }}
                  $isFooter={isFooter}
                  layoutId={!hasAnimated && isMounted && "network_logo"}
                  key="2"
                />
              )}
            </SidePadding>
            <Info
              className={isFooter ? "" : backgroundIsDark ? "lightText" : ""}
            >
              <Name
                style={{
                  justifyContent: !isBig ? "flex-start" : "center",
                  fontSize: isBig ? 28 : isFooter ? 18 : 22,
                }}
              >
                {name.split(" ").map((word, index, allWords) => (
                  <Word
                    key={
                      (!hasAnimated && isMounted && height ? "1_" : "2_") +
                      index
                    }
                    layout={!hasAnimated && isMounted && height}
                  >
                    {word}
                    {index < allWords.length - 1 && "\u00A0"}
                  </Word>
                ))}
              </Name>

              <motion.div
                initial={{ height: isBig ? 4 : 0 }}
                animate={{ height: isBig ? 4 : 0 }}
              />
              <Location
                style={{
                  justifyContent: !isBig ? "flex-start" : "center",
                }}
              >
                <motion.div
                  key={!hasAnimated && isMounted && height ? "1" : "2"}
                  layout={!hasAnimated && isMounted && height}
                >
                  {city}, {state}
                </motion.div>
              </Location>
            </Info>
            <SidePadding
              initial={{ width: isBig ? 40 : 0 }}
              animate={{ width: isBig ? 40 : 0 }}
            />
          </InfoContainer>
        </Contents>
      </Inside>
      {noteInfo && noteInfo.noteID && <NoteInfoPadding />}
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  position: relative;
  padding: 24px;
  padding-bottom: ${({ $isFooter }) => ($isFooter ? "0" : null)};
  box-sizing: border-box;
  ${pageWidth};

  a:hover {
    text-decoration: none !important;
  }
`;
const Inside = styled(motion.div)`
  display: flex;
  align-items: center;
  overflow: visible;
  height: auto;
`;

const Contents = styled.a`
  display: block;
  width: 100%;
  position: relative;
`;

const Logo = styled(motion.div)`
  border-radius: 100%;
  height: 80px;
  width: 80px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.35);
  border: 4px solid white;
  margin: 0;
  flex-grow: 0;
  flex-shrink: 0;
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -40px;
  box-sizing: border-box;
  z-index: 1000;
`;
const LogoSmall = styled(Logo)`
  height: ${(props) => (props.$isFooter ? "50px" : "70px")};
  width: ${(props) => (props.$isFooter ? "50px" : "70px")};
  position: relative;
  bottom: auto;
  left: auto;
  margin-left: 0;
  border: 2px solid #fff;
`;

const Info = styled(motion.div)`
  z-index: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &.lightText {
    color: #f7f7f7;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

const Name = styled(motion.div)`
  font-size: 28px;
  font-weight: 400;
  line-height: 1.2;
  text-align: center;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
`;

const SidePadding = styled(motion.div)`
  width: 40px;
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 70px;
`;

const Location = styled(motion.div)`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Word = styled(motion.div)``;

const TopPadding = styled(motion.div)``;

const NoteInfoPadding = styled.div`
  height: 8px;
`;
